import { useEffect, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  Book,
  Calendar,
  DocumentText1,
  ExportSquare,
  ImportSquare,
  Notepad,
  Profile2User,
} from "iconsax-reactjs";
import StatGradientCard, {
  StatGradientTone,
} from "../../components/commons/stats/StatGradientCard";
import type { GetDashboardDataResponse } from "../../services/webServices/dahsboard/types";
import { getDashboardData } from "../../services/webServices/dahsboard/get";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

const cardsConfig: {
  title: string;
  subtitle: string;
  countKey: keyof GetDashboardDataResponse;
  tone: StatGradientTone;
  path: string;
  icon: ReactNode;
}[] = [
  {
    title: "کاربران",
    subtitle: "تعداد کل ثبت‌نام‌ها",
    countKey: "users_count",
    tone: "orange",
    path: "/user",
    icon: <Profile2User size={22} color="currentColor" variant="Bold" />,
  },
  {
    title: "پست‌ها",
    subtitle: "تعداد کل پست‌ها",
    countKey: "posts_count",
    tone: "pink",
    path: "/post",
    icon: <DocumentText1 size={22} color="currentColor" variant="Bold" />,
  },
  {
    title: "رویدادها",
    subtitle: "تعداد کل رویدادها",
    countKey: "events_count",
    tone: "blue",
    path: "/event",
    icon: <Calendar size={22} color="currentColor" variant="Bold" />,
  },
  {
    title: "مقالات",
    subtitle: "تعداد کل مقالات",
    countKey: "articles_count",
    tone: "purple",
    path: "/article",
    icon: <Book size={22} color="currentColor" variant="Bold" />,
  },
  {
    title: "دوره‌ها",
    subtitle: "تعداد کل دوره‌ها",
    countKey: "courses_count",
    tone: "cyan",
    path: "/course",
    icon: <Notepad size={22} color="currentColor" variant="Bold" />,
  },
  {
    title: "پرداختی‌ها",
    subtitle: "تعداد و مبلغ کل پرداختی‌ها",
    countKey: "payments_count",
    tone: "teal",
    path: "/payout",
    icon: <ExportSquare size={22} color="currentColor" variant="Bold" />,
  },
  {
    title: "برداشت‌ها",
    subtitle: "تعداد و مبلغ کل برداشت",
    countKey: "payouts_count",
    tone: "yellow",
    path: "/payout",
    icon: <ImportSquare size={22} color="currentColor" variant="Bold" />,
  },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] =
    useState<GetDashboardDataResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (isFirstLoad) showLoading();
      setIsLoading(true);

      try {
        const data = await getDashboardData();
        if (!cancelled) setDashboardData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
        if (isFirstLoad) hideLoading();
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
      dir="rtl"
    >
      {cardsConfig.map((card) => (
        <StatGradientCard
          key={card.countKey}
          title={card.title}
          subtitle={card.subtitle}
          value={isLoading ? "—" : dashboardData?.[card.countKey] ?? 0}
          icon={card.icon}
          tone={card.tone}
          onClick={() => navigate(card.path)}
        />
      ))}
    </div>
  );
}
